import { TagGroup } from "@/components/molecules/TagGroup/TagGroup";
import { t } from "@lingui/core/macro";
import { Tag } from "@mm/metx-design-system";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/atoms/Button/Button";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import "./TagGroupWithAdd.scss";

type TagGroupWithAddProps = {
  onAdd: (value: string) => void;
  isAddAtEnd?: boolean;
  capacity?: number;
  tags: React.ReactNode[];
  initialVisibleCount?: number;
  isSideNav?: boolean;
};

export const TagGroupWithAdd: React.FC<TagGroupWithAddProps> = ({
  onAdd,
  isAddAtEnd = true,
  capacity = 20,
  tags,
  initialVisibleCount = 10,
  isSideNav,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isAdding]);

  const hasMoreThanInitial = tags.length > initialVisibleCount;

  const visibleTags = useMemo(() => {
    return isExpanded ? tags : tags.slice(0, initialVisibleCount);
  }, [tags, isExpanded, initialVisibleCount]);

  const generateNewTagLabel = () => {
    let index = 1;
    let label = `New Tag (${index})`;
    const existingLabels = tags
      .map((tag) => {
        if (tag && typeof tag === "object" && "key" in tag && tag.key != null) {
          return String(tag.key);
        }
        if (tag && typeof tag === "object" && "props" in tag && (tag as any).props.label) {
          return (tag as any).props.label;
        }
        return null;
      })
      .filter(Boolean);

    while (existingLabels.includes(label)) {
      index++;
      label = `New Tag (${index})`;
    }
    return label;
  };

  const finishAdding = (value: string) => {
    const name = value.trim();
    if (name) {
      onAdd(name);
    }
    setIsAdding(false);
  };

  const AddControl = (
    <div className="tag-group__add-container">
      <TooltipWrapper tooltipText={t`Create tag`}>
        <Tag
          className="tag-group__add-tag"
          color="green"
          interactive
          selected
          aria-label={t`Create tag`}
          onClick={() => setIsAdding(true)}
        >
          <span className="material-icons">add</span>
        </Tag>
      </TooltipWrapper>
    </div>
  );

  const ShowMoreControl = hasMoreThanInitial && !isExpanded && (
    <div className="tag-group__show-more-container">
      <Button onClick={() => setIsExpanded(true)} buttonType={"clear"} size="small" className="no-padding">
        <span className="material-icons">keyboard_arrow_down</span>
        {t`Show more`}
      </Button>
    </div>
  );

  const ShowLessControl = hasMoreThanInitial && isExpanded && (
    <div className="tag-group__show-more-container">
      <Button onClick={() => setIsExpanded(false)} buttonType={"clear"} size="small" className="no-padding">
        <span className="material-icons">keyboard_arrow_up</span>
        {t`Show less`}
      </Button>
    </div>
  );

  return (
    <TagGroup>
      {!isAddAtEnd && tags.length < capacity && AddControl}
      {isSideNav ? visibleTags : tags}
      {isAddAtEnd && tags.length < capacity && AddControl}

      {isAdding && (
        <Tag color="grey">
          <input
            className="tag-group__input"
            aria-label={`Rename ${generateNewTagLabel()}`}
            placeholder="Type a new name"
            defaultValue={generateNewTagLabel()}
            maxLength={23}
            ref={inputRef}
            onBlur={(event) => finishAdding(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                finishAdding(event.currentTarget.value);
              } else if (event.key === "Escape") {
                event.preventDefault();
                setIsAdding(false);
              }
            }}
          />
        </Tag>
      )}
      {isSideNav && ShowMoreControl}
      {isSideNav && ShowLessControl}
    </TagGroup>
  );
};
